import type { WebIntentDefinition } from './webIntentCatalog'

export interface WebIntentMatch {
  intent: WebIntentDefinition
  score: number
  matchedText: string
  reason: string
}

const normalize = (value: string): string =>
  value.toLowerCase().replace(/[^a-z0-9#\s-]+/g, ' ').replace(/\s+/g, ' ').trim()

const wordsFor = (value: string): Set<string> =>
  new Set(normalize(value).split(/\s+/).filter((word) => word.length >= 3))

const containsPhrase = (haystack: string, phrase: string): boolean => {
  const normalizedPhrase = normalize(phrase)
  return normalizedPhrase.length > 0 && haystack.includes(normalizedPhrase)
}

const phraseScore = (prompt: string, values: string[], weight: number): number =>
  values.reduce((score, value) => score + (containsPhrase(prompt, value) ? weight : 0), 0)

const wordOverlapScore = (promptWords: Set<string>, values: string[], weight: number): number => {
  const catalogWords = new Set<string>()
  for (const value of values) {
    for (const word of wordsFor(value)) catalogWords.add(word)
  }
  let overlap = 0
  for (const word of catalogWords) {
    if (promptWords.has(word)) overlap += 1
  }
  return overlap * weight
}

const scoreIntent = (normalizedPrompt: string, promptWords: Set<string>, intent: WebIntentDefinition): number => {
  const targetTitle = intent.targetRef?.title ?? ''
  const titleScore = containsPhrase(normalizedPrompt, intent.title) ? 7 : 0
  const targetScore = targetTitle && containsPhrase(normalizedPrompt, targetTitle) ? 8 : 0
  const verbScore = phraseScore(normalizedPrompt, intent.verbs, 4)
  const exampleScore = phraseScore(normalizedPrompt, intent.examples, 3)
  const hintScore = phraseScore(normalizedPrompt, intent.matcherHints, 2)
  const overlapScore = wordOverlapScore(promptWords, [
    intent.title,
    intent.description,
    targetTitle,
    ...intent.verbs,
    ...intent.matcherHints,
  ], 0.5)
  return titleScore + targetScore + verbScore + exampleScore + hintScore + overlapScore
}

export const detectWebIntent = (
  prompt: string,
  intents: WebIntentDefinition[],
): WebIntentMatch | null => {
  const normalizedPrompt = normalize(prompt)
  if (!normalizedPrompt) return null
  const promptWords = wordsFor(prompt)
  let best: WebIntentMatch | null = null
  for (const intent of intents) {
    const score = scoreIntent(normalizedPrompt, promptWords, intent)
    if (score < 7) continue
    const match: WebIntentMatch = {
      intent,
      score,
      matchedText: intent.targetRef?.title ?? intent.title,
      reason: `Matched web intent catalog entry ${intent.id}.`,
    }
    if (!best || match.score > best.score) best = match
  }
  return best
}
